import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Truck, Package, User, SendHorizontal, CreditCard, FileText } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RequestsPage = ({ user, token }) => {
  const [hubs, setHubs] = useState([]);
  const [selectedHub, setSelectedHub] = useState(user?.hub_id || "");
  const [loading, setLoading] = useState(false);
  
  // Vehicle form
  const [vehicleNumber, setVehicleNumber] = useState("");
  
  // Material form
  const [materialForm, setMaterialForm] = useState({
    name: "",
    category: "",
    rate: ""
  });
  
  // Staff form
  const [staffForm, setStaffForm] = useState({
    name: "",
    place: "",
    age: "",
    address: "",
    aadhar_number: "",
    pan_number: "",
    aadhar_front_photo: "",
    aadhar_back_photo: "",
    pan_photo: ""
  });

  useEffect(() => {
    fetchHubs();
  }, []);

  const fetchHubs = async () => {
    try {
      const response = await axios.get(`${API}/hubs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHubs(response.data);
      if (response.data.length > 0 && !user?.hub_id) {
        setSelectedHub(response.data[0].id);
      }
    } catch (error) {
      console.error("Error fetching hubs:", error);
    }
  };

  const handleVehicleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/pending/vehicle-numbers`, 
        { vehicle_number: vehicleNumber },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Vehicle number request submitted for approval!");
      setVehicleNumber("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit request");
    } finally {
      setLoading(false);
    }
  };

  const handleMaterialSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/pending/material-types`, 
        {
          name: materialForm.name,
          category: materialForm.category,
          rate: materialForm.rate ? parseFloat(materialForm.rate) : null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Material type request submitted for approval!");
      setMaterialForm({ name: "", category: "", rate: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit request");
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = (field) => (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setStaffForm({ ...staffForm, [field]: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleStaffSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/pending/staff`, 
        {
          hub_id: selectedHub,
          name: staffForm.name,
          place: staffForm.place,
          age: parseInt(staffForm.age),
          address: staffForm.address,
          aadhar_number: staffForm.aadhar_number,
          pan_number: staffForm.pan_number || null,
          aadhar_front_photo: staffForm.aadhar_front_photo || null,
          aadhar_back_photo: staffForm.aadhar_back_photo || null,
          pan_photo: staffForm.pan_photo || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Staff data submitted for approval!");
      setStaffForm({ name: "", place: "", age: "", address: "", aadhar_number: "", pan_number: "", aadhar_front_photo: "", aadhar_back_photo: "", pan_photo: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit request");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6" data-testid="requests-page">
      <div>
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-heading tracking-tight">Submit Requests</h1>
        <p className="text-muted-foreground text-sm sm:text-base mt-1">Request new vehicles, materials, or staff data (requires admin approval)</p>
      </div>

      <Tabs defaultValue="vehicle" className="w-full">
        <TabsList className="grid w-full grid-cols-3 h-auto">
          <TabsTrigger value="vehicle" className="gap-1 sm:gap-2 text-xs sm:text-sm py-2" data-testid="tab-vehicle">
            <Truck className="w-3 h-3 sm:w-4 sm:h-4" />
            <span className="hidden sm:inline">Vehicle</span>
          </TabsTrigger>
          <TabsTrigger value="material" className="gap-1 sm:gap-2 text-xs sm:text-sm py-2" data-testid="tab-material">
            <Package className="w-3 h-3 sm:w-4 sm:h-4" />
            <span className="hidden sm:inline">Material</span>
          </TabsTrigger>
          <TabsTrigger value="staff" className="gap-1 sm:gap-2 text-xs sm:text-sm py-2" data-testid="tab-staff">
            <User className="w-3 h-3 sm:w-4 sm:h-4" />
            <span className="hidden sm:inline">Staff</span>
          </TabsTrigger>
        </TabsList>

        {/* Vehicle Tab */}
        <TabsContent value="vehicle">
          <Card className="shadow-lg border-border/50">
            <CardHeader className="p-4 sm:p-6">
              <CardTitle className="text-lg sm:text-xl flex items-center gap-2">
                <Truck className="w-5 h-5 text-primary" />
                Request New Vehicle
              </CardTitle>
              <CardDescription className="text-sm">
                Submit a new vehicle number for admin approval
              </CardDescription>
            </CardHeader>
            <CardContent className="p-4 sm:p-6 pt-0">
              <form onSubmit={handleVehicleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="vehicle_number" className="text-sm">Vehicle Number</Label>
                  <Input
                    id="vehicle_number"
                    data-testid="request-vehicle-input"
                    type="text"
                    placeholder="e.g., MH-01-AB-1234"
                    value={vehicleNumber}
                    onChange={(e) => setVehicleNumber(e.target.value)}
                    className="h-10"
                    required
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full gap-2"
                  disabled={loading}
                  data-testid="submit-vehicle-request"
                >
                  <SendHorizontal className="w-4 h-4" />
                  {loading ? "Submitting..." : "Submit for Approval"}
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Material Tab */}
        <TabsContent value="material">
          <Card className="shadow-lg border-border/50">
            <CardHeader className="p-4 sm:p-6">
              <CardTitle className="text-lg sm:text-xl flex items-center gap-2">
                <Package className="w-5 h-5 text-primary" />
                Request New Material Type
              </CardTitle>
              <CardDescription className="text-sm">
                Submit a new material type with rate for admin approval
              </CardDescription>
            </CardHeader>
            <CardContent className="p-4 sm:p-6 pt-0">
              <form onSubmit={handleMaterialSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="material_name" className="text-sm">Material Name</Label>
                  <Input
                    id="material_name"
                    data-testid="request-material-name"
                    type="text"
                    placeholder="e.g., PET Bottles"
                    value={materialForm.name}
                    onChange={(e) => setMaterialForm({ ...materialForm, name: e.target.value })}
                    className="h-10"
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="material_category" className="text-sm">Category</Label>
                    <Select
                      value={materialForm.category}
                      onValueChange={(value) => setMaterialForm({ ...materialForm, category: value })}
                    >
                      <SelectTrigger data-testid="request-material-category">
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Plastic">Plastic</SelectItem>
                        <SelectItem value="Paper">Paper</SelectItem>
                        <SelectItem value="Metal">Metal</SelectItem>
                        <SelectItem value="Glass">Glass</SelectItem>
                        <SelectItem value="Organic">Organic</SelectItem>
                        <SelectItem value="E-Waste">E-Waste</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="material_rate" className="text-sm">Rate per kg (₹)</Label>
                    <Input
                      id="material_rate"
                      data-testid="request-material-rate"
                      type="number"
                      step="0.01"
                      placeholder="e.g., 25"
                      value={materialForm.rate}
                      onChange={(e) => setMaterialForm({ ...materialForm, rate: e.target.value })}
                      className="h-10"
                      required
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  className="w-full gap-2"
                  disabled={loading}
                  data-testid="submit-material-request"
                >
                  <SendHorizontal className="w-4 h-4" />
                  {loading ? "Submitting..." : "Submit for Approval"}
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Staff Tab */}
        <TabsContent value="staff">
          <Card className="shadow-lg border-border/50">
            <CardHeader className="p-4 sm:p-6">
              <CardTitle className="text-lg sm:text-xl flex items-center gap-2">
                <User className="w-5 h-5 text-primary" />
                Request New Staff Entry
              </CardTitle>
              <CardDescription className="text-sm">
                Submit new staff data for admin approval
              </CardDescription>
            </CardHeader>
            <CardContent className="p-4 sm:p-6 pt-0">
              <form onSubmit={handleStaffSubmit} className="space-y-4">
                {!user?.hub_id && (
                  <div className="space-y-2">
                    <Label htmlFor="hub" className="text-sm">Hub</Label>
                    <Select value={selectedHub} onValueChange={setSelectedHub}>
                      <SelectTrigger data-testid="request-staff-hub">
                        <SelectValue placeholder="Select Hub" />
                      </SelectTrigger>
                      <SelectContent>
                        {hubs.map((hub) => (
                          <SelectItem key={hub.id} value={hub.id}>
                            {hub.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="staff_name" className="text-sm">Full Name</Label>
                    <Input
                      id="staff_name"
                      data-testid="request-staff-name"
                      type="text"
                      placeholder="Enter name"
                      value={staffForm.name}
                      onChange={(e) => setStaffForm({ ...staffForm, name: e.target.value })}
                      className="h-10"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="staff_age" className="text-sm">Age</Label>
                    <Input
                      id="staff_age"
                      data-testid="request-staff-age"
                      type="number"
                      placeholder="Age"
                      value={staffForm.age}
                      onChange={(e) => setStaffForm({ ...staffForm, age: e.target.value })}
                      className="h-10"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="staff_place" className="text-sm">Place</Label>
                  <Input
                    id="staff_place"
                    data-testid="request-staff-place"
                    type="text"
                    placeholder="Enter place"
                    value={staffForm.place}
                    onChange={(e) => setStaffForm({ ...staffForm, place: e.target.value })}
                    className="h-10"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="staff_address" className="text-sm">Address</Label>
                  <Input
                    id="staff_address"
                    data-testid="request-staff-address"
                    type="text"
                    placeholder="Enter address"
                    value={staffForm.address}
                    onChange={(e) => setStaffForm({ ...staffForm, address: e.target.value })}
                    className="h-10"
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="staff_aadhar" className="text-sm">Aadhar Number</Label>
                    <Input
                      id="staff_aadhar"
                      data-testid="request-staff-aadhar"
                      type="text"
                      placeholder="12-digit Aadhar"
                      value={staffForm.aadhar_number}
                      onChange={(e) => setStaffForm({ ...staffForm, aadhar_number: e.target.value })}
                      className="h-10"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="staff_pan" className="text-sm">PAN Number (Optional)</Label>
                    <Input
                      id="staff_pan"
                      data-testid="request-staff-pan"
                      type="text"
                      placeholder="e.g., ABCDE1234F"
                      value={staffForm.pan_number}
                      onChange={(e) => setStaffForm({ ...staffForm, pan_number: e.target.value })}
                      className="h-10"
                    />
                  </div>
                </div>

                {/* Document Photos */}
                <div className="space-y-3 pt-2 border-t">
                  <Label className="text-sm font-semibold">Document Photos</Label>
                  
                  <div className="grid grid-cols-3 gap-2">
                    {/* Aadhar Front */}
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Aadhar Front</Label>
                      <Input
                        id="req_aadhar_front"
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload('aadhar_front_photo')}
                        className="hidden"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => document.getElementById('req_aadhar_front').click()}
                        className="w-full h-16 flex flex-col gap-1 text-xs"
                      >
                        {staffForm.aadhar_front_photo ? (
                          <img src={staffForm.aadhar_front_photo} alt="Aadhar Front" className="w-10 h-10 object-cover rounded" />
                        ) : (
                          <>
                            <CreditCard className="w-5 h-5" />
                            <span>Front</span>
                          </>
                        )}
                      </Button>
                    </div>

                    {/* Aadhar Back */}
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Aadhar Back</Label>
                      <Input
                        id="req_aadhar_back"
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload('aadhar_back_photo')}
                        className="hidden"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => document.getElementById('req_aadhar_back').click()}
                        className="w-full h-16 flex flex-col gap-1 text-xs"
                      >
                        {staffForm.aadhar_back_photo ? (
                          <img src={staffForm.aadhar_back_photo} alt="Aadhar Back" className="w-10 h-10 object-cover rounded" />
                        ) : (
                          <>
                            <CreditCard className="w-5 h-5" />
                            <span>Back</span>
                          </>
                        )}
                      </Button>
                    </div>

                    {/* PAN */}
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">PAN Card</Label>
                      <Input
                        id="req_pan_photo"
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload('pan_photo')}
                        className="hidden"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => document.getElementById('req_pan_photo').click()}
                        className="w-full h-16 flex flex-col gap-1 text-xs"
                      >
                        {staffForm.pan_photo ? (
                          <img src={staffForm.pan_photo} alt="PAN" className="w-10 h-10 object-cover rounded" />
                        ) : (
                          <>
                            <FileText className="w-5 h-5" />
                            <span>PAN</span>
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full gap-2"
                  disabled={loading}
                  data-testid="submit-staff-request"
                >
                  <SendHorizontal className="w-4 h-4" />
                  {loading ? "Submitting..." : "Submit for Approval"}
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default RequestsPage;
